import { parseApiErrorBody } from "@/api/guards";
import type { ApiErrorBody, BillingMode } from "@/api/types";

export class ApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody;

  constructor(status: number, body: ApiErrorBody, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export function userMessageForError(status: number, body: ApiErrorBody): string {
  const message = body.error ?? "";
  if (status === 401) return "Sign in to continue.";
  if (status === 402) {
    if (body.category && body.category !== "commercial") {
      return message || "A safety or usage limit prevents this action.";
    }
    return "This action is temporarily unavailable.";
  }
  if (status === 403) return "You do not have access to this resource.";
  if (status === 404) return message || "Not found.";
  if (status === 413) return message || "This transcript is too large to summarize.";
  if (status === 429) return message || "Too many requests. Wait a moment and try again.";
  if (status === 503) return "Summary failed. Try again.";
  if (status >= 500) return "Something went wrong. Try again.";
  if (message) return message;
  return "Cannot reach memoru server. Check your connection and try again.";
}

export function messageForApiError(error: ApiError, billingMode: BillingMode): string {
  if (error.status !== 402 || error.body.category && error.body.category !== "commercial") {
    return error.message;
  }
  return billingMode === "enforced"
    ? "Your plan does not currently include this action."
    : "This action is temporarily unavailable. Try again or contact support.";
}

export function normalizeApiError(status: number, rawBody: unknown): ApiError {
  const body = parseApiErrorBody(rawBody);
  return new ApiError(status, body, userMessageForError(status, body));
}

export function isSameOriginPath(path: string): boolean {
  if (!path.startsWith("/")) return false;
  if (path.startsWith("//")) return false;
  if (
    path.includes("\\") ||
    Array.from(path).some((character) => {
      const code = character.charCodeAt(0);
      return code <= 31 || code === 127;
    })
  ) {
    return false;
  }
  if (path.includes("://")) return false;
  try {
    return new URL(path, "https://app.memoru.invalid").origin === "https://app.memoru.invalid";
  } catch {
    return false;
  }
}

export function safeRedirectPath(path: string | null | undefined, fallback = "/"): string {
  if (!path || !isSameOriginPath(path)) return fallback;
  return path;
}
